import AccountController from '@lib/accounts/accountController';
import {changeAccount} from '@lib/accounts/changeAccount';
import {getCurrentAccount} from '@lib/accounts/getCurrentAccount';
import apiManagerProxy from '@lib/apiManagerProxy';
import {saveMessarioAiSession} from '@lib/messarioAi';
import {
  MessarioLaunchAccountLoggedIn,
  MessarioLaunchCoordinator
} from '@lib/messarioLaunch/coordinatorCore';
import {MessarioLaunchFragment} from '@lib/messarioLaunch/fragment';
import {
  clearMessarioLaunchPending,
  getMessarioLaunchPending,
  saveMessarioLaunchPending
} from '@lib/messarioLaunch/pending';
import {saveMessarioOpenPeer} from '@lib/messarioLaunch/openPeer';
import {resolveMessarioLaunch} from '@lib/messarioLaunch/resolve';
import {MessarioLaunchSlot} from '@lib/messarioLaunch/types';
import {clearMessarioLaunchToken, getMessarioLaunchToken} from '@lib/messarioLaunch/token';
import {showMessarioLaunchMessage} from '@lib/messarioLaunch/ui';
import {
  bootstrapMessarioTemplatesFromStorage,
  saveAndLoadMessarioTemplates
} from '@lib/messarioTemplates';
import rootScope from '@lib/rootScope';

async function readAccountSlots(): Promise<MessarioLaunchSlot[]> {
  return Promise.all(([1, 2, 3, 4] as const).map(async(slot) => {
    const account = await AccountController.get(slot);
    return {slot, userId: account.userId ? String(account.userId) : undefined};
  }));
}

/**
 * Fire-and-forget: must NOT block app bootstrap (awaiting here caused white screens).
 * Dialog open is deferred via sessionStorage → appImManager.
 */
export function startMessarioLaunchCoordinator(fragment: MessarioLaunchFragment) {
  const accountLoggedIn = (callback: (event: MessarioLaunchAccountLoggedIn) => void) => {
    rootScope.addEventListener('account_logged_in', callback);
    return () => rootScope.removeEventListener('account_logged_in', callback);
  };

  const coordinator = new MessarioLaunchCoordinator({
    resolve: async(token) => {
      const result = await resolveMessarioLaunch(
        token,
        import.meta.env.VITE_MESSARIO_PANEL_ORIGIN
      );

      if(result.templates_access) {
        void saveAndLoadMessarioTemplates(
          result.account.external_user_id,
          result.templates_access
        );
      }

      if(result.ai_bundle) {
        saveMessarioAiSession({
          account_id: result.account.account_id,
          panel_user_id: result.account.panel_user_id,
          external_user_id: result.account.external_user_id,
          ai_bundle: result.ai_bundle
        });
      }

      return result;
    },
    readSlots: readAccountSlots,
    hasSomeonePremium: () => apiManagerProxy.hasSomeonePremium(),
    currentSlot: getCurrentAccount,
    // Hard document navigate — avoid appNavigationController.reload (Navigation API queue → white screen).
    changeSlot: (slot) => changeAccount(slot, {hardNavigate: true}),
    openDialog: (peer) => {
      // Persist only — do not touch location.hash before IM is ready (breaks Navigation API / white screen).
      saveMessarioOpenPeer(peer);
    },
    loadPending: () => getMessarioLaunchPending(),
    savePending: (pending) => saveMessarioLaunchPending(pending),
    clearPending: () => clearMessarioLaunchPending(),
    loadToken: () => getMessarioLaunchToken(),
    clearToken: () => clearMessarioLaunchToken(),
    onAccountLoggedIn: accountLoggedIn,
    showMessage: showMessarioLaunchMessage,
    now: Date.now
  });

  void (async() => {
    await coordinator.start(fragment);
    if(!fragment.token) {
      void bootstrapMessarioTemplatesFromStorage();
    }
  })();
}

// HMR boundary for the Messario modules reached only from `src/index.ts` through this entry:
// an edit under `@lib/messario*` updates hot instead of reloading the page (same as appImManager).
// The running coordinator keeps its closures; a page reload picks the new code up.
if(import.meta.hot) import.meta.hot.accept();
